/**
 * StatementCard.js
 * Individual statement card component for the statements list
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Stack,
  Chip,
  LinearProgress,
  Avatar,
  Badge,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  MoreVert as MoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Timeline as TimelineIcon,
  AccountBalance as BankIcon,
  DateRange as DateIcon,
  Receipt as TransactionIcon,
  PlayArrow as StartIcon,
} from '@mui/icons-material';
import { getReconciliationProgress, formatDate } from './statementUtils';

const StatementCard = ({
  statement,
  isSelected,
  onStatementSelect,
  onMenuOpen,
  theme,
  isMobile,
}) => {
  const progress = getReconciliationProgress(statement);

  const getReconciliationChip = () => {
    if (progress === 100) {
      return (
        <Chip
          icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
          label="Concluído"
          size="small"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            color: 'white',
            fontWeight: 600,
            fontSize: '0.65rem',
            height: 20,
            borderRadius: 1.5,
            boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`,
            '& .MuiChip-icon': {
              color: 'white',
            },
          }}
        />
      );
    } else if (progress > 0) {
      return (
        <Chip
          icon={<TimelineIcon sx={{ fontSize: 12 }} />}
          label={`${progress.toFixed(0)}%`}
          size="small"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
            color: 'white',
            fontWeight: 600,
            fontSize: '0.65rem',
            height: 20,
            borderRadius: 1.5,
            boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.3)}`,
            '& .MuiChip-icon': {
              color: 'white',
            },
          }}
        />
      );
    } else {
      return (
        <Chip
          icon={<UncheckedIcon sx={{ fontSize: 12 }} />}
          label="Pendente"
          size="small"
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.grey[500]} 0%, ${theme.palette.grey[700]} 100%)`,
            color: 'white',
            fontWeight: 600,
            fontSize: '0.65rem',
            height: 20,
            borderRadius: 1.5,
            boxShadow: `0 2px 8px ${alpha(theme.palette.grey[500], 0.3)}`,
            '& .MuiChip-icon': {
              color: 'white',
            },
          }}
        />
      );
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        background: isSelected
          ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
              theme.palette.primary.light,
              0.04
            )} 100%)`
          : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(
              theme.palette.background.default,
              0.6
            )} 100%)`,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${
          isSelected ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.divider, 0.1)
        }`,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
        '&::before': isSelected
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            }
          : {},
      }}
      onClick={() => onStatementSelect(statement)}
      onDoubleClick={() => onStatementSelect(statement)}
    >
      <CardContent sx={{ p: { xs: 1, sm: 1.5 }, '&:last-child': { pb: { xs: 1, sm: 1.5 } } }}>
        {/* Selection Indicator */}
        {isSelected && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          />
        )}

        <Stack direction="row" spacing={1} alignItems="flex-start">
          {/* File Icon with Status */}
          <Box sx={{ position: 'relative' }}>
            <Avatar
              sx={{
                width: { xs: 28, sm: 32 },
                height: { xs: 28, sm: 32 },
                background:
                  progress === 100
                    ? `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`
                    : progress > 0
                    ? `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`
                    : `linear-gradient(135deg, ${theme.palette.grey[400]} 0%, ${theme.palette.grey[600]} 100%)`,
                color: 'white',
                boxShadow: `0 2px 8px ${alpha(
                  progress === 100
                    ? theme.palette.success.main
                    : progress > 0
                    ? theme.palette.warning.main
                    : theme.palette.grey[500],
                  0.3
                )}`,
              }}
            >
              <FileIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
            </Avatar>

            {/* Status Indicator */}
            <Box
              sx={{
                position: 'absolute',
                bottom: -3,
                right: -3,
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: 'background.paper',
                border: `2px solid ${theme.palette.background.paper}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.1)}`,
              }}
            >
              {progress === 100 ? (
                <CheckCircleIcon sx={{ fontSize: 10, color: theme.palette.success.main }} />
              ) : progress > 0 ? (
                <TimelineIcon sx={{ fontSize: 10, color: theme.palette.warning.main }} />
              ) : (
                <Badge
                  variant="dot"
                  color="error"
                  sx={{
                    '& .MuiBadge-dot': {
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                    },
                  }}
                >
                  <Box sx={{ width: 6, height: 6 }} />
                </Badge>
              )}
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={0.5}>
              {/* Header with File Name and Status */}
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="flex-start"
                justifyContent="space-between"
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="700"
                    sx={{
                      fontSize: { xs: '0.85rem', sm: '0.9rem' },
                      lineHeight: 1.2,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      mb: 0.25,
                    }}
                  >
                    {statement.fileName}
                  </Typography>

                  {/* Bank Account */}
                  {statement.bankAccount?.name && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <BankIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.7rem',
                        }}
                      >
                        {statement.bankAccount.name}
                      </Typography>
                    </Stack>
                  )}
                </Box>

                {/* Status Chip */}
                <Box sx={{ flexShrink: 0 }}>{getReconciliationChip()}</Box>
              </Stack>

              {/* Date Range and Transaction Count */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 0.25, sm: 0.5 }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <DateIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {formatDate(statement.periodStart || statement.statementStartDate)} -{' '}
                    {formatDate(statement.periodEnd || statement.statementEndDate)}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <TransactionIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {statement.totalTransactions || 0} transações
                  </Typography>
                </Stack>
              </Stack>

              {/* Progress Bar for Partial Reconciliation */}
              {progress > 0 && progress < 100 && (
                <Box sx={{ mt: 0.5 }}>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        flex: 1,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.warning.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.warning.main,
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ minWidth: 30, fontSize: '0.65rem' }}
                    >
                      {progress.toFixed(0)}%
                    </Typography>
                  </Stack>
                </Box>
              )}

              {/* Upload Date */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.65rem', mt: 0.25 }}
              >
                Upload:{' '}
                {formatDate(statement.createdAt || statement.dateCreate || statement.uploadDate)}
              </Typography>
            </Stack>
          </Box>

          {/* Actions Menu */}
          <Box sx={{ flexShrink: 0 }}>
            <Stack direction="row" spacing={0.25}>
              {/* Quick Start Button */}
              <Tooltip title="Iniciar Conciliação">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatementSelect(statement);
                  }}
                  sx={{
                    p: 0.5,
                    color: 'primary.main',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      color: 'primary.dark',
                    },
                  }}
                >
                  <StartIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>

              {/* More Options Menu */}
              <IconButton
                size="small"
                onClick={(e) => onMenuOpen(e, statement)}
                sx={{
                  p: 0.5,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <MoreIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default StatementCard;
